import React from 'react'
import { Text, View } from 'react-native'
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, Checkbox } from '@material-ui/core'

interface PDTableProps {
  containerStyle?: React.CSSProperties
  headCellStyle?: React.CSSProperties
  headRowStyle?: React.CSSProperties
  headCell: { id: number; label: string }[]
  rowCell: { id: number; label: string }[]
  data: any[]
  hasCheckbox?: boolean
  hasCheckAll?: boolean
  checkboxColor?: string
  stickyHeader?: boolean
  getStyle?: (cell: string, value: any, row?: any) => React.CSSProperties
  renderCell?: (cell: string, value: any, row: any, rowIndex: number) => React.ReactNode | undefined
  renderHeadCell?: (cell: { id: number; label: string }, index: number) => React.ReactNode
  getHeadCellProps?: (
    cell: { id: number; label: string },
    index: number
  ) => { colSpan?: number; style?: React.CSSProperties; hide?: boolean }
  getCellProps?: (
    cell: string,
    row: any,
    rowIndex: number
  ) => { rowSpan?: number; colSpan?: number; style?: React.CSSProperties; hide?: boolean }
  pagination?: {
    page: number
    totalPages: number
    totalElements: number
    first: boolean
    last: boolean
    onPageChange: (page: number) => void
  }
  onSelectionChange?: (rows: any[], selectedRows: any[]) => void
  allRowsSelected?: boolean
  onSelectAllChange?: (selected: boolean) => void
}

export default function PDTable({
  containerStyle,
  headCellStyle,
  headCell,
  headRowStyle,
  rowCell,
  data,
  hasCheckbox,
  hasCheckAll,
  checkboxColor,
  stickyHeader,
  allRowsSelected = false,
  pagination,
  getStyle,
  renderCell,
  renderHeadCell,
  getHeadCellProps,
  getCellProps,
  onSelectionChange,
  onSelectAllChange
}: PDTableProps) {
  const pageItems = pagination ? getPageItems(pagination.page, pagination.totalPages) : []

  return (
    <View>
      <TableContainer component={Paper} style={{ borderRadius: 16, overflowX: 'auto', ...containerStyle }}>
        <Table stickyHeader={stickyHeader} style={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            <TableRow style={{ backgroundColor: '#dce1e3', fontWeight: 'bold', ...headRowStyle }}>
              {hasCheckAll && (
                <TableCell>
                  <Checkbox
                    checked={allRowsSelected}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    style={{ color: checkboxColor ? checkboxColor : '#ED1B2E' }}
                    onChange={(event) => {
                      const checked = event.target.checked
                      const nextRows = data.map((row) => ({ ...row, checked }))
                      if (onSelectAllChange) {
                        onSelectAllChange(checked)
                      } else {
                        onSelectionChange?.(nextRows, checked ? nextRows : [])
                      }
                    }}
                  />
                </TableCell>
              )}
              {headCell.map((cell, index) => {
                const cellProps = getHeadCellProps ? getHeadCellProps(cell, index) : undefined

                if (cellProps?.hide) {
                  return null
                }

                return (
                  <TableCell
                    key={cell.id}
                    colSpan={cellProps?.colSpan}
                    style={{ fontWeight: '600', ...headCellStyle, ...cellProps?.style }}
                  >
                    {renderHeadCell ? renderHeadCell(cell, index) : cell.label}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={rowCell.length + (hasCheckbox ? 1 : 0)}
                  style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    height: 110,
                    borderBottomWidth: 0
                  }}
                >
                  Không tìm được thông tin theo yêu cầu
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {hasCheckbox && (
                    <TableCell>
                      <Checkbox
                        checked={Boolean(row.checked)}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        style={{ color: checkboxColor ? checkboxColor : '#ED1B2E' }}
                        onChange={(event) => {
                          const nextRows = data.map((currentRow) =>
                            currentRow === row ? { ...currentRow, checked: event.target.checked } : currentRow
                          )
                          onSelectionChange?.(
                            nextRows,
                            nextRows.filter((currentRow) => currentRow.checked)
                          )
                        }}
                      />
                    </TableCell>
                  )}
                  {rowCell.map((cell) => {
                    const cellProps = getCellProps ? getCellProps(cell.label, row, rowIndex) : undefined

                    if (cellProps?.hide) {
                      return null
                    }

                    const customContent = renderCell
                      ? renderCell(cell.label, row[cell.label], row, rowIndex)
                      : undefined

                    return (
                      <TableCell
                        key={cell.id}
                        rowSpan={cellProps?.rowSpan}
                        colSpan={cellProps?.colSpan}
                        style={cellProps?.style}
                      >
                        <View>
                          {customContent !== undefined ? (
                            customContent
                          ) : (
                            <Text style={(getStyle ? getStyle(cell.label, row[cell.label], row) : {}) as any}>
                              {row[cell.label]}
                            </Text>
                          )}
                        </View>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && pagination.totalPages > 1 && (
        <View style={styles.pagination}>
          <button
            type="button"
            aria-label="Trang đầu"
            title="Trang đầu"
            style={styles.pageButton}
            disabled={pagination.first}
            onClick={() => pagination.onPageChange(0)}
          >
            «
          </button>
          <button
            type="button"
            aria-label="Trang trước"
            title="Trang trước"
            style={styles.pageButton}
            disabled={pagination.first}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
          >
            ‹
          </button>
          {pageItems.map((item, index) =>
            item === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} style={styles.ellipsis}>
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                aria-label={`Trang ${item + 1}`}
                style={{
                  ...styles.pageButton,
                  ...(item === pagination.page ? styles.activePageButton : {})
                }}
                onClick={() => pagination.onPageChange(item)}
              >
                {item + 1}
              </button>
            )
          )}
          <button
            type="button"
            aria-label="Trang sau"
            title="Trang sau"
            style={styles.pageButton}
            disabled={pagination.last}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
          >
            ›
          </button>
          <button
            type="button"
            aria-label="Trang cuối"
            title="Trang cuối"
            style={styles.pageButton}
            disabled={pagination.last}
            onClick={() => pagination.onPageChange(pagination.totalPages - 1)}
          >
            »
          </button>
        </View>
      )}
    </View>
  )
}

const getPageItems = (currentPage: number, totalPages: number): Array<number | 'ellipsis'> => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index)
  }

  if (currentPage <= 2) {
    return [0, 1, 2, 'ellipsis', totalPages - 1]
  }

  if (currentPage >= totalPages - 3) {
    return [0, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1]
  }

  return [0, 'ellipsis', currentPage, 'ellipsis', totalPages - 1]
}

const styles: Record<string, any> = {
  pagination: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16
  },
  pageButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e5e7eb',
    borderWidth: 0,
    borderRadius: 20,
    color: '#6b7280',
    display: 'flex',
    fontSize: 14,
    height: 25,
    padding: 0,
    width: 25
  },
  activePageButton: {
    backgroundColor: '#ED1B2E',
    color: '#ffffff'
  },
  ellipsis: {
    alignItems: 'center',
    color: '#6b7280',
    display: 'flex',
    fontSize: 16,
    height: 40,
    justifyContent: 'center',
    width: 20
  }
}
